// viewer.controls.js — custom <video> controls bar.
// Replaces native <video controls> to eliminate Safari's full-frame
// darkening overlay (spec §1) and to keep the bar zoom-stable.
//
// This module is split into pure helpers (top) and DOM/wire-up code
// (mountControls at bottom) for unit-testability. See
// docs/superpowers/specs/2026-05-26-custom-video-controls-design.md

import { mountThumbnail } from "./viewer.controls.thumbnail.js";

const pad2 = (n) => String(n).padStart(2, "0");

export function formatTime(s) {
  if (!Number.isFinite(s) || s < 0) return "0:00";
  const t = Math.floor(s);
  const hh = Math.floor(t / 3600);
  const mm = Math.floor((t % 3600) / 60);
  const ss = t % 60;
  return hh > 0 ? `${hh}:${pad2(mm)}:${pad2(ss)}` : `${mm}:${pad2(ss)}`;
}

export function formatTimePair(current, duration, mode) {
  const d = Number.isFinite(duration) && duration > 0 ? duration : 0;
  const cRaw = Number.isFinite(current) && current > 0 ? current : 0;
  // When duration is unknown (d=0), still show the current position rather
  // than clamping it to 0.
  const c = d > 0 ? Math.min(cRaw, d) : cRaw;
  // If duration >= 1h, both sides render as H:MM:SS so the slash stays aligned.
  const useHours = d >= 3600;
  const fmt = (s) => {
    if (!useHours) return formatTime(s);
    const t = Math.max(0, Math.floor(s));
    return `${Math.floor(t / 3600)}:${pad2(Math.floor((t % 3600) / 60))}:${pad2(t % 60)}`;
  };
  const left = fmt(c);
  if (mode === "remaining") return `${left} / -${fmt(d - c)}`;
  return `${left} / ${fmt(d)}`;
}

export function pointerXToTime(x, width, duration) {
  if (!Number.isFinite(duration) || duration <= 0 || !Number.isFinite(width) || width <= 0
      || !Number.isFinite(x)) {
    return 0;
  }
  const frac = Math.max(0, Math.min(1, x / width));
  return frac * duration;
}

export function timeToProgressPercent(current, duration) {
  if (!Number.isFinite(duration) || duration <= 0 || !Number.isFinite(current)) return 0;
  const frac = Math.max(0, Math.min(1, current / duration));
  return frac * 100;
}

export function bufferedCoverPercent(buffered, currentTime, duration) {
  if (!Number.isFinite(duration) || duration <= 0 || !buffered) return 0;
  for (let i = 0; i < buffered.length; i++) {
    const start = buffered.start(i);
    const end = buffered.end(i);
    if (currentTime >= start && currentTime <= end) {
      return Math.max(0, Math.min(100, (end / duration) * 100));
    }
  }
  return 0;
}

// Visibility state machine (spec §4.1). Pure reducer; the side-effecting
// idle timer and event listeners that drive it live in mountControls.
export const IDLE_HIDE_MS = 2500;

export function initialVisibilityState() {
  return {
    visible: true,        // start visible so user sees the bar exists
    playing: false,       // tracked from <video>'s play/pause events
    hovering: false,      // pointer inside .vmc-video-clip
    focused: false,       // focus inside .vmc-controls
    menuOpen: false,      // speed/settings menu open
    dragging: false,      // scrub or volume drag in progress
    lastActivityAt: 0,    // ms timestamp of last show-triggering activity
  };
}

// Capability detection helpers (spec §5.1, §5.2, §5.4).
// Pure functions — no side effects, no DOM access; safe to call from tests.

export function hasFullscreen(element, video) {
  if (element && (element.requestFullscreen || element.webkitRequestFullscreen)) return true;
  if (video && video.webkitEnterFullscreen) return true;
  return false;
}

export function hasPictureInPicture(video) {
  if (!video) return false;
  if (typeof video.requestPictureInPicture === "function") return true;
  if (typeof video.webkitSetPresentationMode === "function"
      && typeof video.webkitSupportsPresentationMode === "function"
      && video.webkitSupportsPresentationMode("picture-in-picture")) {
    return true;
  }
  return false;
}

export function isVolumeReadonly(video) {
  if (!video) return false;
  const original = video.volume;
  const probe = original < 0.5 ? 0.9 : 0.1; // pick something different
  try { video.volume = probe; } catch { return true; }
  const readonly = video.volume !== probe;
  try { video.volume = original; } catch { /* ignore */ }
  return readonly;
}

// 5× and 10× skim long flights (EnergyCo ask, 2026-09-10). Browsers mute
// audio at those rates and a 4K HLS stream drops to its lowest rung.
export const SPEED_STEPS = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2, 5, 10];

// SVG glyphs (inline so no external assets). Kept simple/monochrome.
const SVG = {
  play:    '<svg viewBox="0 0 24 24" width="20" height="20"><path fill="currentColor" d="M8 5v14l11-7z"/></svg>',
  pause:   '<svg viewBox="0 0 24 24" width="20" height="20"><path fill="currentColor" d="M6 5h4v14H6zM14 5h4v14h-4z"/></svg>',
  skipBack:'<svg viewBox="0 0 24 24" width="20" height="20"><path fill="currentColor" d="M11 18V6l-8.5 6 8.5 6zM11.5 12l8.5 6V6l-8.5 6z"/></svg>',
  skipFwd: '<svg viewBox="0 0 24 24" width="20" height="20"><path fill="currentColor" d="M13 6v12l8.5-6L13 6zM4 18l8.5-6L4 6v12z"/></svg>',
  volume:  '<svg viewBox="0 0 24 24" width="20" height="20"><path fill="currentColor" d="M3 9v6h4l5 5V4L7 9H3zm13.5 3a4.5 4.5 0 0 0-2.5-4v8a4.5 4.5 0 0 0 2.5-4z"/></svg>',
  mute:    '<svg viewBox="0 0 24 24" width="20" height="20"><path fill="currentColor" d="M16.5 12a4.5 4.5 0 0 0-2.5-4v2.18L16.45 12l-2.45 2.27V16a4.5 4.5 0 0 0 2.5-4zM3 9v6h4l5 5V4L7 9H3z"/></svg>',
  pip:     '<svg viewBox="0 0 24 24" width="20" height="20"><path fill="currentColor" d="M19 11h-8v6h8v-6zm2-7H3a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h18a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2zm0 14H3V6h18v12z"/></svg>',
  fs:      '<svg viewBox="0 0 24 24" width="20" height="20"><path fill="currentColor" d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"/></svg>',
};

function buildControlsDOM() {
  const root = document.createElement("div");
  root.className = "vmc-controls";
  root.innerHTML = `
    <div class="vmc-controls-scrim" aria-hidden="true"></div>
    <div class="vmc-controls-bar" data-tour="viewer.controls">
      <div class="vmc-controls-timeline-wrap" data-tour="viewer.timeline">
        <div class="vmc-controls-thumbnail" hidden>
          <canvas width="160" height="90"></canvas>
          <div class="vmc-controls-thumbnail-time">0:00</div>
        </div>
        <div class="vmc-controls-timeline" role="slider"
             aria-label="Seek" aria-valuemin="0" aria-valuemax="100" aria-valuenow="0">
          <div class="vmc-controls-timeline-buffered"></div>
          <div class="vmc-controls-timeline-progress"></div>
          <div class="vmc-controls-timeline-thumb"></div>
        </div>
      </div>
      <div class="vmc-controls-buttons">
        <div class="vmc-controls-left">
          <button type="button" data-act="playpause" aria-label="Play" data-tour="viewer.playpause">${SVG.play}</button>
          <button type="button" data-act="skipback" aria-label="Back 15 seconds" data-tour="viewer.skipback">${SVG.skipBack}</button>
          <button type="button" data-act="skipfwd" aria-label="Forward 15 seconds">${SVG.skipFwd}</button>
          <div class="vmc-controls-volume">
            <button type="button" data-act="mute" aria-label="Mute">${SVG.volume}</button>
            <input type="range" min="0" max="100" value="100" aria-label="Volume" />
          </div>
          <div class="vmc-controls-time" role="button" tabindex="0" aria-label="Toggle time display">0:00 / 0:00</div>
        </div>
        <div class="vmc-controls-right">
          <button type="button" data-act="speed" aria-label="Playback speed" data-tour="viewer.speed">1×</button>
          <button type="button" data-act="pip" aria-label="Picture in Picture">${SVG.pip}</button>
          <button type="button" data-act="fullscreen" aria-label="Fullscreen" data-tour="viewer.fullscreen">${SVG.fs}</button>
        </div>
      </div>
    </div>
    <div class="vmc-controls-speed-menu" hidden></div>
  `;
  return root;
}

export function mountControls(video, opts) {
  const clip = opts.clip;
  if (!clip || !video) throw new Error("mountControls: clip and video are required");

  const root = buildControlsDOM();
  clip.appendChild(root);

  let state = initialVisibilityState();
  state.playing = !video.paused;

  const apply = () => {
    root.classList.toggle("vmc-controls-hidden", !state.visible);
  };

  const dispatch = (event) => {
    state = reduceVisibility(state, event);
    apply();
  };

  // Date.now() (not performance.now()) so vitest's fake timers can drive the
  // idle clock — vi.useFakeTimers() fakes Date but leaves performance real.
  const now = () => Date.now();

  // Idle timer — re-evaluates every 250ms while playing. Cheap; runs only
  // while at least one mountControls instance is alive.
  const idleTimer = setInterval(() => {
    dispatch({ type: "idle", now: now() });
  }, 250);

  const onEnter = () => dispatch({ type: "pointerenter" });
  const onMove  = () => dispatch({ type: "pointermove", now: now() });
  const onLeave = () => dispatch({ type: "pointerleave" });
  const onFocusIn  = (e) => { if (root.contains(e.target)) dispatch({ type: "focusin" }); };
  const onFocusOut = (e) => { if (root.contains(e.target)) dispatch({ type: "focusout" }); };
  const onPlay  = () => dispatch({ type: "play", now: now() });
  const onPause = () => dispatch({ type: "pause" });
  const onKey   = (e) => {
    if (e.key === "Escape") { dispatch({ type: "escape" }); return; }
    // other keybindings land in Task 17/18; until then, any key resets idle
    dispatch({ type: "keydown", now: now() });
  };

  clip.addEventListener("pointerenter", onEnter);
  clip.addEventListener("pointermove", onMove);
  clip.addEventListener("pointerleave", onLeave);
  clip.addEventListener("focusin", onFocusIn);
  clip.addEventListener("focusout", onFocusOut);
  video.addEventListener("play", onPlay);
  video.addEventListener("pause", onPause);
  document.addEventListener("keydown", onKey);

  let pendingClickTimer = null;
  const CLICK_DBL_MS = 250;

  const togglePlayPause = () => {
    if (video.paused) video.play().catch(() => {});
    else video.pause();
  };

  const toggleFullscreen = () => {
    const fsTarget = clip;
    const req = fsTarget.requestFullscreen || fsTarget.webkitRequestFullscreen;
    const exit = document.exitFullscreen || document.webkitExitFullscreen;
    const current = document.fullscreenElement || document.webkitFullscreenElement;
    if (current) { if (exit) exit.call(document); return; }
    // iOS Safari fallback — only works on the <video> itself
    if (!req && typeof video.webkitEnterFullscreen === "function") {
      try { video.webkitEnterFullscreen(); } catch { /* ignore */ }
      return;
    }
    if (req) { try { req.call(fsTarget); } catch { /* ignore */ } }
  };

  // Touch sends a synthesized `click` after `pointerup`. Suppress it for a
  // short window after a touch tap so onClipClickTouchAware doesn't fire
  // play/pause on top of the double-tap halves handler below. Uses Date.now()
  // (via now()) so vitest fake timers can drive it deterministically — perf
  // timers stay on the real wall clock under fake timers.
  let suppressNextClickUntil = 0;
  const onClipClickTouchAware = (e) => {
    if (now() < suppressNextClickUntil) return;
    if (root.contains(e.target)) return;
    if (pendingClickTimer !== null) {
      clearTimeout(pendingClickTimer);
      pendingClickTimer = null;
      toggleFullscreen();
      return;
    }
    pendingClickTimer = setTimeout(() => {
      pendingClickTimer = null;
      togglePlayPause();
    }, CLICK_DBL_MS);
  };
  clip.addEventListener("click", onClipClickTouchAware);

  // Pointer-based tap detection on the <video> element (spec §4.4). Tracks
  // touch taps separately from mouse clicks so we can implement double-tap
  // halves without also firing the desktop single-click play/pause toggle.
  let lastTapAt = 0;
  const DBL_TAP_MS = 300;
  const onVideoPointerUp = (e) => {
    if (e.pointerType !== "touch") return;
    // Suppress the click that follows this pointerup so the click handler
    // above doesn't queue a second single-tap action.
    suppressNextClickUntil = now() + 500;
    const rect = clip.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const width = rect.width;
    const elapsed = now() - lastTapAt;
    if (elapsed < DBL_TAP_MS) {
      // Double-tap — halves vs centre
      lastTapAt = 0;
      const frac = width > 0 ? x / width : 0;
      if (frac >= 0.35 && frac <= 0.65) {
        toggleFullscreen();
      } else if (frac < 0.35) {
        skipBy(-15);
      } else {
        skipBy(+15);
      }
      return;
    }
    lastTapAt = now();
    // Schedule the single-tap action (show + play/pause) after the
    // double-tap window expires, in case no second tap arrives.
    const scheduledAt = lastTapAt;
    setTimeout(() => {
      if (lastTapAt === scheduledAt) {
        togglePlayPause();
        dispatch({ type: "pointermove", now: now() });
      }
    }, DBL_TAP_MS + 10);
  };
  video.addEventListener("pointerup", onVideoPointerUp);

  const btnPlay = root.querySelector('button[data-act="playpause"]');
  const timeEl  = root.querySelector(".vmc-controls-time");

  let timeMode = localStorage.getItem("vmc.timeMode") === "remaining" ? "remaining" : "total";

  // Drag-scrub state — declared up here because refreshTime/refreshProgress
  // (defined below, run at mount) consult it to yield to the pointer.
  let drag = null; // { startX, dragging } | null
  const isDragScrubbing = () => drag !== null && drag.dragging;

  const setPlayGlyph = (playing) => {
    if (playing) {
      btnPlay.innerHTML = SVG.pause;
      btnPlay.setAttribute("aria-label", "Pause");
    } else {
      btnPlay.innerHTML = SVG.play;
      btnPlay.setAttribute("aria-label", "Play");
    }
  };
  const refreshTime = () => {
    if (isDragScrubbing()) return;
    timeEl.textContent = formatTimePair(video.currentTime, video.duration, timeMode);
  };

  setPlayGlyph(!video.paused);
  refreshTime();

  const onBtnPlayClick = (e) => { e.stopPropagation(); togglePlayPause(); };
  const onTimeClick    = (e) => {
    e.stopPropagation();
    timeMode = timeMode === "total" ? "remaining" : "total";
    localStorage.setItem("vmc.timeMode", timeMode);
    refreshTime();
  };
  const onTimeUpdate     = () => refreshTime();
  const onDurationChange = () => refreshTime();
  const onPlayGlyph      = () => setPlayGlyph(true);
  const onPauseGlyph     = () => setPlayGlyph(false);

  btnPlay.addEventListener("click", onBtnPlayClick);
  timeEl.addEventListener("click", onTimeClick);
  video.addEventListener("timeupdate", onTimeUpdate);
  video.addEventListener("durationchange", onDurationChange);

  // also re-render the glyph from play/pause events (in addition to existing listeners)
  video.addEventListener("play", onPlayGlyph);
  video.addEventListener("pause", onPauseGlyph);

  const timelineWrap = root.querySelector(".vmc-controls-timeline-wrap");
  const progressEl   = root.querySelector(".vmc-controls-timeline-progress");
  const bufferedEl   = root.querySelector(".vmc-controls-timeline-buffered");
  const thumbEl      = root.querySelector(".vmc-controls-timeline-thumb");

  const refreshProgress = () => {
    if (isDragScrubbing()) return;
    const pct = timeToProgressPercent(video.currentTime, video.duration);
    progressEl.style.width = `${pct}%`;
    thumbEl.style.left = `${pct}%`;
  };
  const refreshBuffered = () => {
    const pct = bufferedCoverPercent(video.buffered, video.currentTime, video.duration);
    bufferedEl.style.width = `${pct}%`;
  };

  refreshProgress();
  refreshBuffered();

  const previewEl = clip.querySelector(".vmc-preview");
  const seekTo = (t) => {
    const ct = Math.max(0, Math.min(t, video.duration || 0));
    video.currentTime = ct;
    if (previewEl) { try { previewEl.currentTime = ct; } catch { /* ignore */ } }
  };

  // ---- Drag-scrub seek coalescing ----
  // The main video takes at most one in-flight seek; the newest drag position
  // wins when the previous seek completes ("seeked"). Long unbuffered
  // progressive files otherwise stall the seek engine and the UI with it.
  // Same latest-wins shape as viewer.controls.thumbnail.js requestSeek.
  let mainSeekInFlight = false;
  let pendingMainSeekT = null;
  const requestMainSeek = (t) => {
    if (mainSeekInFlight) { pendingMainSeekT = t; return; }
    // Arm only once the write succeeded — a throwing currentTime setter
    // (pre-metadata on some browsers) must not leave the coalescer armed.
    // Safe ordering: `seeked` is always a queued task, never synchronous.
    video.currentTime = t;
    mainSeekInFlight = true;
  };
  const onSeekedDrain = () => {
    mainSeekInFlight = false;
    if (pendingMainSeekT !== null) {
      const t = pendingMainSeekT;
      pendingMainSeekT = null;
      requestMainSeek(t);
    }
  };
  video.addEventListener("seeked", onSeekedDrain);

  // Pointer-driven UI during a drag: thumb, fill and clock render from the
  // pointer's timeline position — never blocked on the video.
  const renderScrubUI = (t) => {
    const pct = timeToProgressPercent(t, video.duration);
    progressEl.style.width = `${pct}%`;
    thumbEl.style.left = `${pct}%`;
    timeEl.textContent = formatTimePair(t, video.duration, timeMode);
  };

  const DRAG_THRESHOLD_PX = 4;

  const scrubTimeFromClientX = (clientX) => {
    const rect = timelineWrap.getBoundingClientRect();
    return pointerXToTime(clientX - rect.left, rect.width, video.duration);
  };
  const seekFromClientX = (clientX) => { seekTo(scrubTimeFromClientX(clientX)); };

  const onTimelinePointerDown = (e) => {
    e.stopPropagation();
    drag = { startX: e.clientX, dragging: false };
    try { timelineWrap.setPointerCapture(e.pointerId); } catch { /* not supported */ }
  };
  const onTimelinePointerMove = (e) => {
    if (drag === null) return;
    if (!drag.dragging && Math.abs(e.clientX - drag.startX) >= DRAG_THRESHOLD_PX) {
      drag.dragging = true;
      // Start every drag from a disarmed coalescer. Both end paths disarm it
      // too, so this covers the remaining case: a `seeked` that never fires
      // *within* a drag (e.g. a seek issued before metadata loaded) would
      // otherwise leave the flag armed for the rest of this drag.
      mainSeekInFlight = false;
      pendingMainSeekT = null;
      clip.classList.add("vmc-scrubbing");
      dispatch({ type: "dragstart" });
    }
    if (drag.dragging) {
      const t = scrubTimeFromClientX(e.clientX);
      renderScrubUI(t);
      if (previewEl) { try { previewEl.currentTime = t; } catch { /* ignore */ } }
      requestMainSeek(t);
    }
  };
  const onTimelinePointerEnd = (e) => {
    if (drag === null) return;
    if (e.type === "pointercancel") {
      // Cancel always aborts — don't commit a seek that the user didn't intend.
      if (drag.dragging) {
        clip.classList.remove("vmc-scrubbing");
        dispatch({ type: "dragend", now: now() });
      }
      // No commit on cancel, but disarm like the release path does — the
      // coalescer must never stay armed once no drag owns it.
      pendingMainSeekT = null;
      mainSeekInFlight = false;
    } else if (!drag.dragging) {
      // plain click — seek to the up position
      seekFromClientX(e.clientX);
    } else {
      clip.classList.remove("vmc-scrubbing");
      dispatch({ type: "dragend", now: now() });
      pendingMainSeekT = null;
      mainSeekInFlight = false; // direct write below replaces any in-flight seek
      seekFromClientX(e.clientX);
    }
    drag = null;
    refreshProgress();
    refreshTime();
    try { timelineWrap.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
  };

  timelineWrap.addEventListener("pointerdown", onTimelinePointerDown);
  timelineWrap.addEventListener("pointermove", onTimelinePointerMove);
  timelineWrap.addEventListener("pointerup", onTimelinePointerEnd);
  timelineWrap.addEventListener("pointercancel", onTimelinePointerEnd);
  video.addEventListener("timeupdate", refreshProgress);
  video.addEventListener("durationchange", refreshProgress);
  video.addEventListener("progress", refreshBuffered);
  video.addEventListener("timeupdate", refreshBuffered);

  const btnMute   = root.querySelector('button[data-act="mute"]');
  const volSlider = root.querySelector('.vmc-controls-volume input[type="range"]');

  let lastNonZero = 1;
  // Load persisted volume.
  const savedVol = parseFloat(localStorage.getItem("vmc.volume") ?? "");
  if (Number.isFinite(savedVol) && savedVol >= 0 && savedVol <= 1) {
    video.volume = savedVol;
    if (savedVol > 0) lastNonZero = savedVol;
  }

  // Hide volume slider on iOS (volume is read-only there; mute still works).
  if (isVolumeReadonly(video)) {
    volSlider.hidden = true;
  }

  const refreshVolumeUI = () => {
    const muted = video.muted || video.volume === 0;
    btnMute.innerHTML = muted ? SVG.mute : SVG.volume;
    btnMute.setAttribute("aria-label", muted ? "Unmute" : "Mute");
    if (!volSlider.hidden) volSlider.value = String(Math.round(video.volume * 100));
  };
  refreshVolumeUI();

  const onVolInput = (e) => {
    e.stopPropagation();
    const v = parseInt(volSlider.value, 10) / 100;
    video.volume = v;
    video.muted = v === 0;
    if (v > 0) lastNonZero = v;
    localStorage.setItem("vmc.volume", String(v));
    refreshVolumeUI();
  };
  const onMuteClick = (e) => {
    e.stopPropagation();
    if (video.muted || video.volume === 0) {
      video.muted = false;
      const restore = lastNonZero > 0 ? lastNonZero : 1;
      video.volume = restore;
      localStorage.setItem("vmc.volume", String(restore));
    } else {
      video.muted = true;
    }
    refreshVolumeUI();
  };
  const onVolumeChange = () => refreshVolumeUI();

  volSlider.addEventListener("input", onVolInput);
  btnMute.addEventListener("click", onMuteClick);
  video.addEventListener("volumechange", onVolumeChange);

  const btnSpeed = root.querySelector('button[data-act="speed"]');
  const speedMenu = root.querySelector(".vmc-controls-speed-menu");

  // Populate the menu once.
  speedMenu.innerHTML = SPEED_STEPS.map((s) =>
    `<button type="button" data-speed="${s}">${s}×</button>`
  ).join("");

  const refreshSpeedUI = () => {
    btnSpeed.textContent = `${video.playbackRate}×`;
    [...speedMenu.querySelectorAll("button")].forEach((b) => {
      const isCurrent = parseFloat(b.dataset.speed) === video.playbackRate;
      b.setAttribute("aria-checked", isCurrent ? "true" : "false");
    });
  };
  refreshSpeedUI();

  const openSpeedMenu = () => {
    speedMenu.hidden = false;
    dispatch({ type: "menuopen" });
  };
  const closeSpeedMenu = () => {
    speedMenu.hidden = true;
    dispatch({ type: "menuclose" });
  };

  const onBtnSpeedClick = (e) => {
    e.stopPropagation();
    speedMenu.hidden ? openSpeedMenu() : closeSpeedMenu();
  };
  const onSpeedMenuClick = (e) => {
    e.stopPropagation();
    const btn = e.target.closest("button[data-speed]");
    if (!btn) return;
    video.playbackRate = parseFloat(btn.dataset.speed);
    refreshSpeedUI();
    closeSpeedMenu();
  };
  const onDocClickClose = (e) => {
    if (speedMenu.hidden) return;
    if (speedMenu.contains(e.target) || btnSpeed.contains(e.target)) return;
    closeSpeedMenu();
  };
  const onRateChange = () => refreshSpeedUI();

  btnSpeed.addEventListener("click", onBtnSpeedClick);
  speedMenu.addEventListener("click", onSpeedMenuClick);
  document.addEventListener("click", onDocClickClose);
  video.addEventListener("ratechange", onRateChange);

  const btnSkipBack = root.querySelector('button[data-act="skipback"]');
  const btnSkipFwd  = root.querySelector('button[data-act="skipfwd"]');

  const skipBy = (delta) => {
    const t = (video.currentTime || 0) + delta;
    seekTo(t);
  };

  const onSkipBack = (e) => { e.stopPropagation(); skipBy(-15); };
  const onSkipFwd  = (e) => { e.stopPropagation(); skipBy(+15); };

  btnSkipBack.addEventListener("click", onSkipBack);
  btnSkipFwd.addEventListener("click", onSkipFwd);

  const btnFs = root.querySelector('button[data-act="fullscreen"]');
  if (!hasFullscreen(clip, video)) {
    btnFs.hidden = true;
  } else {
    const onFsClick = (e) => { e.stopPropagation(); toggleFullscreen(); };
    btnFs.addEventListener("click", onFsClick);
    // Track listener for dispose
    btnFs._onClick = onFsClick;
  }

  const btnPip = root.querySelector('button[data-act="pip"]');
  if (!hasPictureInPicture(video)) {
    btnPip.hidden = true;
  } else {
    const togglePip = () => {
      // Safari path
      if (typeof video.webkitSetPresentationMode === "function") {
        const next = video.webkitPresentationMode === "picture-in-picture" ? "inline" : "picture-in-picture";
        try { video.webkitSetPresentationMode(next); } catch { /* ignore */ }
        return;
      }
      // Standard path
      if (document.pictureInPictureElement === video) {
        if (typeof document.exitPictureInPicture === "function") {
          document.exitPictureInPicture().catch(() => {});
        }
      } else {
        video.requestPictureInPicture().catch(() => {});
      }
    };
    const onPipClick = (e) => { e.stopPropagation(); togglePip(); };
    btnPip.addEventListener("click", onPipClick);
    btnPip._onClick = onPipClick;
  }

  // Keyboard shortcuts (spec §4.3). Replaces the simple Escape-handler.
  const SHORTCUT_KEYS = new Set([
    " ", "k", "K", "j", "J", "l", "L",
    "ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown",
    "m", "M", "f", "F", "i", "I",
    ">", "<",
    "Escape",
  ]);

  const adjustVolume = (delta) => {
    const v = Math.max(0, Math.min(1, video.volume + delta));
    video.volume = v;
    // Use epsilon comparison — IEEE-754 accumulation (e.g., 1.0 minus 0.1 ten
    // times) leaves a tiny positive residue, not 0.
    video.muted = v < 1e-10;
    if (v > 1e-10) lastNonZero = v;
    localStorage.setItem("vmc.volume", String(v));
    refreshVolumeUI();
  };

  const cycleSpeed = (dir) => {
    const idx = SPEED_STEPS.indexOf(video.playbackRate);
    if (idx < 0) { video.playbackRate = 1; refreshSpeedUI(); return; }
    const next = Math.max(0, Math.min(SPEED_STEPS.length - 1, idx + dir));
    video.playbackRate = SPEED_STEPS[next];
    refreshSpeedUI();
  };

  const onKeyComprehensive = (e) => {
    if (e.key === "Escape") {
      if (!speedMenu.hidden) closeSpeedMenu();
      dispatch({ type: "escape" });
      return;
    }
    if (!SHORTCUT_KEYS.has(e.key)) return;

    // Suppress in editable fields.
    const ae = document.activeElement;
    if (ae && (ae.tagName === "INPUT" || ae.tagName === "TEXTAREA" || ae.isContentEditable)) {
      return;
    }
    // Otherwise page-global: the window having focus is enough (operator
    // request 2026-08-21 — hover-gating made spacebar redundant). One
    // carve-out: a focused Leaflet map keeps the arrow keys for panning.
    if (e.key.startsWith("Arrow") && ae && ae.closest?.(".leaflet-container")) {
      return;
    }

    e.preventDefault();
    dispatch({ type: "keydown", now: now() });

    switch (e.key) {
      case " ": case "k": case "K": togglePlayPause(); break;
      case "j": case "J": skipBy(-15); break;
      case "l": case "L": skipBy(+15); break;
      case "ArrowLeft":  skipBy(-5); break;
      case "ArrowRight": skipBy(+5); break;
      case "ArrowUp":    adjustVolume(+0.1); break;
      case "ArrowDown":  adjustVolume(-0.1); break;
      case "m": case "M": onMuteClick({ stopPropagation() {} }); break;
      case "f": case "F": if (!btnFs.hidden) toggleFullscreen(); break;
      case "i": case "I": if (!btnPip.hidden && btnPip._onClick) btnPip._onClick({ stopPropagation() {} }); break;
      case ">": cycleSpeed(+1); break;
      case "<": cycleSpeed(-1); break;
      default: /* no-op */
    }
  };

  // Replace the existing keydown listener.
  document.removeEventListener("keydown", onKey);
  document.addEventListener("keydown", onKeyComprehensive);

  // Hover-thumbnail (spec §4.6) — opt-in via opts.previewSrc.
  const thumbnailHandle = mountThumbnail({
    wrap: timelineWrap,
    previewSrc: opts.previewSrc,
    duration: () => video.duration,
  });

  apply();

  return {
    dispose() {
      if (pendingClickTimer !== null) clearTimeout(pendingClickTimer);
      clip.removeEventListener("click", onClipClickTouchAware);
      video.removeEventListener("pointerup", onVideoPointerUp);
      clearInterval(idleTimer);
      clip.classList.remove("vmc-scrubbing");
      drag = null;
      clip.removeEventListener("pointerenter", onEnter);
      clip.removeEventListener("pointermove", onMove);
      clip.removeEventListener("pointerleave", onLeave);
      clip.removeEventListener("focusin", onFocusIn);
      clip.removeEventListener("focusout", onFocusOut);
      video.removeEventListener("play", onPlay);
      video.removeEventListener("pause", onPause);
      document.removeEventListener("keydown", onKeyComprehensive);
      btnPlay.removeEventListener("click", onBtnPlayClick);
      timeEl.removeEventListener("click", onTimeClick);
      video.removeEventListener("timeupdate", onTimeUpdate);
      video.removeEventListener("durationchange", onDurationChange);
      video.removeEventListener("play", onPlayGlyph);
      video.removeEventListener("pause", onPauseGlyph);
      timelineWrap.removeEventListener("pointerdown", onTimelinePointerDown);
      timelineWrap.removeEventListener("pointermove", onTimelinePointerMove);
      timelineWrap.removeEventListener("pointerup", onTimelinePointerEnd);
      timelineWrap.removeEventListener("pointercancel", onTimelinePointerEnd);
      video.removeEventListener("seeked", onSeekedDrain);
      video.removeEventListener("timeupdate", refreshProgress);
      video.removeEventListener("durationchange", refreshProgress);
      video.removeEventListener("progress", refreshBuffered);
      video.removeEventListener("timeupdate", refreshBuffered);
      volSlider.removeEventListener("input", onVolInput);
      btnMute.removeEventListener("click", onMuteClick);
      video.removeEventListener("volumechange", onVolumeChange);
      btnSpeed.removeEventListener("click", onBtnSpeedClick);
      speedMenu.removeEventListener("click", onSpeedMenuClick);
      document.removeEventListener("click", onDocClickClose);
      video.removeEventListener("ratechange", onRateChange);
      btnSkipBack.removeEventListener("click", onSkipBack);
      btnSkipFwd.removeEventListener("click", onSkipFwd);
      if (btnFs._onClick) btnFs.removeEventListener("click", btnFs._onClick);
      if (btnPip._onClick) btnPip.removeEventListener("click", btnPip._onClick);
      thumbnailHandle.dispose();
      root.remove();
    },
    setPlaybackRate(rate) { video.playbackRate = rate; },
  };
}

export function reduceVisibility(state, event) {
  switch (event.type) {
    case "pointerenter":
      return { ...state, visible: true, hovering: true };
    case "pointermove":
      return { ...state, visible: true, lastActivityAt: event.now ?? state.lastActivityAt };
    case "pointerleave": {
      const visible = state.playing ? false : state.visible;
      return { ...state, hovering: false, visible };
    }
    case "play":
      return { ...state, playing: true, lastActivityAt: event.now ?? state.lastActivityAt };
    case "pause":
      return { ...state, playing: false, visible: true };
    case "focusin":
      return { ...state, focused: true, visible: true };
    case "focusout":
      return { ...state, focused: false };
    case "keydown":
      return { ...state, visible: true, lastActivityAt: event.now ?? state.lastActivityAt };
    case "dragstart":
      return { ...state, dragging: true, visible: true };
    case "dragend":
      return { ...state, dragging: false, lastActivityAt: event.now ?? state.lastActivityAt };
    case "menuopen":
      return { ...state, menuOpen: true, visible: true };
    case "menuclose":
      return { ...state, menuOpen: false };
    case "escape":
      return { ...state, visible: false, menuOpen: false };
    case "idle": {
      const idleFor = (event.now ?? state.lastActivityAt) - state.lastActivityAt;
      const canHide = state.playing
        && !state.focused
        && !state.hovering
        && !state.menuOpen
        && !state.dragging
        && idleFor >= IDLE_HIDE_MS;
      return canHide ? { ...state, visible: false } : state;
    }
    default:
      return state;
  }
}
